import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { FilmmakerLocation } from '@/types';

interface UseFilmmakerLocationRealtimeOptions {
  bookingId: string | undefined;
  onLocationUpdate?: (location: FilmmakerLocation | null) => void;
  enabled?: boolean;
}

/**
 * Hook to listen for real-time filmmaker location updates
 * Subscribes to filmmaker_locations table changes via Supabase Realtime
 */
export function useFilmmakerLocationRealtime({
  bookingId,
  onLocationUpdate,
  enabled = true,
}: UseFilmmakerLocationRealtimeOptions) {
  const [latestLocation, setLatestLocation] = useState<FilmmakerLocation | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!enabled || !bookingId) {
      return;
    }

    // Verify bookingId is a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(bookingId)) {
      return;
    }

    // Fetch latest location first
    const fetchLatestLocation = async () => {
      try {
        const { data, error } = await supabase
          .from('filmmaker_locations')
          .select('*')
          .eq('booking_id', bookingId)
          .order('location_timestamp', { ascending: false })
          .limit(1);

        if (error) {
          // If error is "no rows" (PGRST116), that's okay - just means no location yet
          if (error.code === 'PGRST116') {
            return;
          }
          console.error('Error fetching latest location:', error);
          return;
        }

        if (data && data.length > 0) {
          const location = data[0] as FilmmakerLocation;
          setLatestLocation(location);
          if (onLocationUpdate) {
            onLocationUpdate(location);
          }
        } else {
          // No location data yet
          if (onLocationUpdate) {
            onLocationUpdate(null);
          }
        }
      } catch (err) {
        console.error('Error fetching latest location:', err);
      }
    };

    fetchLatestLocation();

    // Subscribe to new location updates
    const channel = supabase
      .channel(`filmmaker-location-${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'filmmaker_locations',
          filter: `booking_id=eq.${bookingId}`,
        },
        (payload) => {
          const location = payload.new as FilmmakerLocation;
          setLatestLocation(location);
          if (onLocationUpdate) {
            onLocationUpdate(location);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Filmmaker location Realtime subscription error');
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setLatestLocation(null);
    };
  }, [bookingId, enabled, onLocationUpdate]);

  return latestLocation;
}

